import { useState, useEffect, useCallback } from "react";
import { withTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';

import api from "../../../api/api";
import countriesData from "../../Config/countries.json";
import UserModals from "../Users/components/UserModals";
import {
    fetchChats,
    fetchHistory,
    selectChat,
    sendMessage
} from "./actions";

import WhatsAppSidebar from "./components/WhatsAppSidebar";
import WhatsAppChatWindow from "./components/WhatsAppChatWindow";
import WhatsAppModals from "./components/WhatsAppModals";
import "./WhatsApp.css";

const VALID_COUNTRY_CODES = new Set(countriesData.countries.map(c => c.value.toLowerCase()));

const countryPrefixes = {};
countriesData.countries.forEach(c => {
    if (c.phoneCode && c.value) {
        countryPrefixes[c.phoneCode] = c.value.toLowerCase();
    }
});
countryPrefixes["1"] = "us";


const WhatsApp = ({ t, match, history }) => {
    const dispatch = useDispatch();

    // Redux selectors
    const {
        chats,
        activeChat,
        messages,
        loadingChats,
        loadingHistory,
        sendingMessage
    } = useSelector(state => state.whatsapp || {});

    // Local state for UI states
    const [newMessage, setNewMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [modalPhone, setModalPhone] = useState("");
    const [modalCountry, setModalCountry] = useState("il");
    const [modalFirstName, setModalFirstName] = useState("");
    const [modalLastName, setModalLastName] = useState("");
    const [checkingPhone, setCheckingPhone] = useState(false);
    const [checkResult, setCheckResult] = useState(null);
    const [showUserInfoModal, setShowUserInfoModal] = useState(false);
    const [userInfoData, setUserInfoData] = useState(null);
    const [loadingUserInfo, setLoadingUserInfo] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // UserModals integration state
    const [activeModalUser, setActiveModalUser] = useState(null);

    const handleSelectChat = (chat) => {
        const phone = chat.id.split('@')[0];
        if (history) {
            history.push(`/ControlPanel/WhatsApp/${phone}`);
        }
        dispatch(selectChat(chat));
    };

    // Initial load
    useEffect(() => {
        dispatch(fetchChats());
    }, [dispatch]);

    const phoneParam = match?.params?.phone;

    // URL phone parameter sync to select active chat
    useEffect(() => {
        if (!loadingChats && chats) {
            if (phoneParam) {
                const cleanParam = phoneParam.replace(/\D/g, '');
                if (cleanParam) {
                    const activePhone = activeChat?.id?.split('@')[0];
                    if (activePhone !== cleanParam) {
                        const matchedChat = chats.find(c => c.id.split('@')[0] === cleanParam);
                        if (matchedChat) {
                            dispatch(selectChat(matchedChat));
                        } else {
                            dispatch(selectChat({
                                id: `${cleanParam}@c.us`,
                                name: phoneParam,
                                avatar: null,
                                image: null,
                                lastMessage: null
                            }));
                        }
                    }
                }
            } else if (activeChat) {
                // If we navigate to the base path, clear the active chat selection
                dispatch({ type: 'WHATSAPP_SELECT_CHAT', chat: null });
            }
        }
    }, [phoneParam, chats, loadingChats, activeChat, dispatch]);

    // Polling active chat message history every 10 seconds
    useEffect(() => {
        let pollInterval;
        if (activeChat) {
            pollInterval = setInterval(() => {
                dispatch(fetchHistory(activeChat.id, false));
            }, 10000);
        }
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [activeChat, dispatch]);

    // Country flags mapping
    const getCountryFlag = useCallback((countryCode) => {
        if (!countryCode) return "";
        const code = countryCode.toLowerCase();
        const found = countriesData.countries.find(c => c.value === code || c.alpha3?.toLowerCase() === code);
        return found ? found.icon : `/assets/flags/32x32/${code}.png`;
    }, []);

    const getPhotoUrl = useCallback((photo) => {
        if (!photo) return null;
        if (photo.startsWith("http://") || photo.startsWith("https://") || photo.startsWith("data:")) {
            return photo;
        }
        if (photo.startsWith("/assets/")) {
            return photo;
        }
        const baseUrl = process.env.REACT_APP_BASE_URL || "";
        const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
        const cleanPhoto = photo.startsWith("/") ? photo : "/" + photo;
        return cleanBaseUrl + cleanPhoto;
    }, []);

    // Resolving country from phone code via countries.json validation
    const getCountryFromWhatsAppId = useCallback((chatId) => {
        if (!chatId) return null;
        const phone = chatId.split('@')[0];

        const prefix = Object.keys(countryPrefixes)
            .sort((a, b) => b.length - a.length)
            .find(pref => phone.startsWith(pref));
        if (prefix) {
            const countryCode = countryPrefixes[prefix];
            return VALID_COUNTRY_CODES.has(countryCode) ? countryCode : null;
        }
        return null;
    }, []);

    // Operations
    const handleCheckWhatsApp = () => {
        setCheckingPhone(true);
        setCheckResult(null);
        api.get(`/green/check?phone=${modalPhone}&country=${modalCountry}`)
            .then(response => {
                setCheckingPhone(false);
                setCheckResult(response.data.exists);
            })
            .catch(error => {
                setCheckingPhone(false);
                setCheckResult(false);
                toast.error(t("Pages.WhatsApp.errorOccurred"));
            });
    };

    const handleAddContactSubmit = (e) => {
        e.preventDefault();
        api.post('/green/contact/add', {
            phone: modalPhone,
            country: modalCountry,
            firstName: modalFirstName,
            lastName: modalLastName
        }, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => {
                if (response.data && response.data.success) {
                    toast.success(t("Pages.WhatsApp.successAdd"));
                    setShowAddModal(false);
                    setModalPhone("");
                    setModalFirstName("");
                    setModalLastName("");
                    setCheckResult(null);
                    dispatch(fetchChats());
                } else {
                    toast.error(t("Pages.WhatsApp.errorOccurred"));
                }
            })
            .catch(error => {
                toast.error(t("Pages.WhatsApp.errorOccurred"));
            });
    };

    const handleEditContactSubmit = (e) => {
        e.preventDefault();
        api.post('/green/contact/edit', {
            chatId: activeChat.id,
            firstName: modalFirstName,
            lastName: modalLastName
        }, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => {
                if (response.data && response.data.success) {
                    toast.success(t("Pages.WhatsApp.successEdit"));
                    setShowEditModal(false);
                    setModalFirstName("");
                    setModalLastName("");

                    const updatedChat = { ...activeChat, name: modalFirstName.trim() + " " + modalLastName.trim() };
                    dispatch({ type: 'WHATSAPP_SELECT_CHAT', chat: updatedChat });
                    dispatch(fetchChats());
                } else {
                    toast.error(t("Pages.WhatsApp.errorOccurred"));
                }
            })
            .catch(error => {
                toast.error(t("Pages.WhatsApp.errorOccurred"));
            });
    };

    const handleDeleteContact = () => {
        if (window.confirm(t("Pages.WhatsApp.deleteContactConfirm"))) {
            api.post('/green/contact/delete', {
                chatId: activeChat.id
            }, {
                headers: { 'Content-Type': 'application/json' }
            })
                .then(response => {
                    if (response.data && response.data.success) {
                        toast.success(t("Pages.WhatsApp.successDelete"));
                        dispatch({ type: 'WHATSAPP_SELECT_CHAT', chat: null });
                        dispatch(fetchChats());
                    } else {
                        toast.error(t("Pages.WhatsApp.errorOccurred"));
                    }
                })
                .catch(error => {
                    toast.error(t("Pages.WhatsApp.errorOccurred"));
                });
        }
    };

    const mapUserInfoToDbUser = (info) => {
        if (!info || info.isWhatsAppOnly) return null;
        return {
            ...info,
            Id: info.Id !== undefined ? info.Id : info.id,
            Name: info.Name !== undefined ? info.Name : `${info.firstName || ""} ${info.lastName || ""}`.trim(),
            FirstName: info.FirstName !== undefined ? info.FirstName : info.firstName,
            LastName: info.LastName !== undefined ? info.LastName : info.lastName,
            Username: info.Username !== undefined ? info.Username : info.username,
            Email: info.Email !== undefined ? info.Email : info.email,
            Phone: info.Phone !== undefined ? info.Phone : info.phone,
            Country: info.Country !== undefined ? info.Country : info.country,
            Photo: info.Photo !== undefined ? info.Photo : info.photo,
            UserType: info.UserType !== undefined ? info.UserType : {
                Id: info.userTypeId,
                Name: info.userTypeName
            },
            IsActive: info.IsActive !== undefined ? info.IsActive : info.isActive,
            IsLocked: info.IsLocked !== undefined ? info.IsLocked : info.isLocked,
            IsSuperTeacher: info.IsSuperTeacher !== undefined ? info.IsSuperTeacher : info.isSuperTeacher,
            CreationDate: info.CreationDate !== undefined ? info.CreationDate : info.creationDate,
            UpdateDate: info.UpdateDate !== undefined ? info.UpdateDate : info.updateDate,
            School: info.School !== undefined ? info.School : (info.schoolName ? { Name: info.schoolName } : null),
            Birthday: info.Birthday !== undefined ? info.Birthday : info.birthday,
            Sex: info.Sex !== undefined ? info.Sex : info.sex,
            Address: info.Address !== undefined ? info.Address : info.address,
            MaxSubUser: info.MaxSubUser !== undefined ? info.MaxSubUser : info.maxSubUser,
            notesCount: info.Notes !== undefined ? info.Notes : (info.notesCount !== undefined ? info.notesCount : 0),
            NotesCount: info.Notes !== undefined ? info.Notes : (info.NotesCount !== undefined ? info.NotesCount : 0)
        };
    };

    const refreshData = useCallback(() => {
        dispatch(fetchChats());
        if (activeChat) {
            api.get(`/green/user-info?chatId=${activeChat.id}`)
                .then(response => {
                    const info = {
                        ...response.data,
                        photo: response.data.photo || activeChat.avatar || activeChat.image || null
                    };
                    const dbUser = mapUserInfoToDbUser(info);
                    if (dbUser) {
                        setActiveModalUser(dbUser);
                    }
                })
                .catch(() => { });
        }
    }, [dispatch, activeChat]);

    const handleShowUserInfo = (chat = null) => {
        const targetChat = chat || activeChat;
        if (!targetChat) return;

        setLoadingUserInfo(true);
        setUserInfoData(null);
        api.get(`/green/user-info?chatId=${targetChat.id}`)
            .then(response => {
                const info = {
                    ...response.data,
                    photo: response.data.photo || targetChat.avatar || targetChat.image || null
                };
                setLoadingUserInfo(false);

                // Cache info on chat object in Redux to dynamically render registration badges
                const updatedChats = chats.map(c => {
                    if (c.id === targetChat.id) {
                        return { ...c, systemUser: info };
                    }
                    return c;
                });
                dispatch({ type: 'WHATSAPP_UPDATE_CHATS', chats: updatedChats });

                if (!info.isWhatsAppOnly) {
                    // System user exists! Show modular system user modal
                    const dbUser = mapUserInfoToDbUser(info);
                    setActiveModalUser(dbUser);
                } else {
                    // Unregistered/WhatsApp only user, show the custom info modal
                    setUserInfoData(info);
                    setShowUserInfoModal(true);
                }
            })
            .catch(error => {
                setUserInfoData({
                    isWhatsAppOnly: true,
                    firstName: targetChat.name || targetChat.id.split('@')[0],
                    lastName: "",
                    phone: targetChat.id.split('@')[0],
                    id: targetChat.id,
                    photo: targetChat.avatar || targetChat.image || null,
                    country: targetChat.systemUser && targetChat.systemUser.country ? targetChat.systemUser.country : getCountryFromWhatsAppId(targetChat.id)
                });
                setLoadingUserInfo(false);
                setShowUserInfoModal(true);
            });
    };

    return (
        <>
            <Helmet>
                <title>{t("Pages.WhatsApp.title")} | {t("alarabeyya")}</title>
            </Helmet>

            <div className="whatsapp-page-wrapper">
                {!sidebarOpen && (
                    <div
                        className="mobile-sidebar-toggle-btn d-md-none"
                        onClick={() => setSidebarOpen(true)}
                        title={t("Pages.Chatbot.OpenMenu") || "فتح القائمة"}
                    >
                        <i className="fal fa-bars"></i>
                    </div>
                )}
                <div className="whatsapp-container">
                    <WhatsAppSidebar
                        chats={chats}
                        activeChat={activeChat}
                        loadingChats={loadingChats}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                        onAddContactClick={() => {
                            setShowAddModal(true);
                            setModalPhone("");
                            setModalFirstName("");
                            setModalLastName("");
                            setCheckResult(null);
                        }}
                        onSyncClick={() => dispatch(fetchChats(true))}
                        onSelectChat={handleSelectChat}
                        onShowUserInfo={handleShowUserInfo}
                        getPhotoUrl={getPhotoUrl}
                        getCountryFlag={getCountryFlag}
                        getCountryFromWhatsAppId={getCountryFromWhatsAppId}
                        t={t}
                    />

                    {activeChat ? (
                        <WhatsAppChatWindow
                            activeChat={activeChat}
                            messages={messages}
                            loadingHistory={loadingHistory}
                            sendingMessage={sendingMessage}
                            newMessage={newMessage}
                            setNewMessage={setNewMessage}
                            onSendMessage={(e) => {
                                e.preventDefault();
                                if (newMessage.trim()) {
                                    dispatch(sendMessage(activeChat.id, newMessage.trim(), t));
                                    setNewMessage("");
                                }
                            }}
                            onShowUserInfo={handleShowUserInfo}
                            onEditContactClick={(fName, lName) => {
                                setShowEditModal(true);
                                setModalFirstName(fName);
                                setModalLastName(lName);
                            }}
                            onDeleteContactClick={handleDeleteContact}
                            onBackToList={() => {
                                if (history) {
                                    history.push('/ControlPanel/WhatsApp');
                                }
                            }}
                            getPhotoUrl={getPhotoUrl}
                            getCountryFlag={getCountryFlag}
                            getCountryFromWhatsAppId={getCountryFromWhatsAppId}
                            t={t}
                        />
                    ) : (
                        <div className="whatsapp-welcome-screen">
                            <div className="whatsapp-welcome-content">
                                <i className="fab fa-whatsapp whatsapp-welcome-icon"></i>
                                <h4 className="mt-3">{t("Pages.WhatsApp.welcomeTitle")}</h4>
                                <p className="text-muted mt-2">{t("Pages.WhatsApp.welcomeText")}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <WhatsAppModals
                showAddModal={showAddModal}
                setShowAddModal={setShowAddModal}
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
                showUserInfoModal={showUserInfoModal}
                setShowUserInfoModal={setShowUserInfoModal}
                activeChat={activeChat}
                userInfoData={userInfoData}
                setUserInfoData={setUserInfoData}
                loadingUserInfo={loadingUserInfo}
                checkingPhone={checkingPhone}
                setCheckingPhone={setCheckingPhone}
                checkResult={checkResult}
                setCheckResult={setCheckResult}
                modalPhone={modalPhone}
                setModalPhone={setModalPhone}
                modalCountry={modalCountry}
                setModalCountry={setModalCountry}
                modalFirstName={modalFirstName}
                setModalFirstName={setModalFirstName}
                modalLastName={modalLastName}
                setModalLastName={setModalLastName}
                handleCheckWhatsApp={handleCheckWhatsApp}
                handleAddContactSubmit={handleAddContactSubmit}
                handleEditContactSubmit={handleEditContactSubmit}
                getPhotoUrl={getPhotoUrl}
                getCountryFlag={getCountryFlag}
                getCountryFromWhatsAppId={getCountryFromWhatsAppId}
                fetchChats={() => dispatch(fetchChats())}
                t={t}
            />

            {/* User Profile Modals Integration */}
            <UserModals
                activeUser={activeModalUser}
                onClose={() => setActiveModalUser(null)}
                onSuccess={refreshData}
                t={t}
            />
        </>
    );
};

export default withTranslation('common')(WhatsApp);
